import React from 'react';
import Box from '@mui/material/Box';
import { Button, Paper, Typography } from '@mui/material';
import ResponsiveAppBar from '../components/ResponsiveAppBar';
import { useNavigate } from 'react-router-dom';

const UniverseSection: React.FC = () => {
  const navigate = useNavigate();
  
  const handleNavClick = (id: string): void => {
    navigate("/", { state: { scrollToId: id } });
  };

  return (
    <Box id="universe">
      <ResponsiveAppBar onNavClick={handleNavClick} />
      
      {/* Hero Section - The Great Divide */}
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700}>
          THE SHARD UNIVERSE
        </Typography>
        <Typography fontSize={{ xs: 24, sm: 30, md: 40 }} fontWeight={700} sx={{color: '#55DEF1', marginBottom: '3%'}}>
          The Great Divide
        </Typography>
        
        <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
          Once, twelve empires ruled the stars. Nine have vanished into history, consumed by silence, war, or the unending dark. Only three remain, linked by the ancient Gate Network, a relic older than any surviving record. Through these gates move trade, armies, ambition, and the quiet machinery of sabotage and betrayal.
        </Typography><br/>
        
        <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
          For centuries, a fragile peace has held. Yet inside the shared Senate where the surviving empires govern, rivalries have sharpened to a dangerous edge. Leadership rotates every twenty years. The Federation holds it now, and under their Emperor’s rule, border conflicts have erupted, colonies have burned, and truth has been reshaped into propaganda.
        </Typography><br/>
        
        <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
          Three empires remain. Three powers that decide the fate of the galaxy. Their next conflict will determine whether the stars stay divided or fall into fire.
        </Typography>
      </Box>

      {/* The Three Empires */}
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700} sx={{marginBottom: '3%'}}>
          THE THREE EMPIRES
        </Typography>
        
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          height: '100%',
          gap: { xs: 2, md: 0 }
        }}>
          {/* Aurellian Sovereignty */}
          <Paper
            sx={{
              flex: 1,
              margin: { xs: '1%', md: '2%' },
              padding: { xs: '4%', md: '3%' },
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              boxShadow: 3,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography fontSize={{ xs: 20, sm: 24, md: 28 }} fontWeight={700} sx={{color: '#55DEF1', marginBottom: '2%'}}>
              Aurellian Sovereignty
            </Typography>
            <Box sx={{
              width: '100%',
              height: { xs: 150, md: 200 },
              backgroundColor: 'rgba(85, 222, 241, 0.2)',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #55DEF1',
              marginBottom: '3%'
            }}>
              <img 
                src='/images/Faction1.webp' 
                alt="Aurellian Sovereignty"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '2px',
                }}
              />
            </Box>
            <Typography fontSize={{ xs: 16, sm: 18, md: 20 }} fontWeight={400}>
              A realm of nobility and tradition, the Sovereignty spans sixty-eight star systems. From Thallia's radiant cities to the core worlds of Grellion and Lassa Mir, it is an empire of beauty, strength, and bloodlines stretching back millennia.
            </Typography><br/>
            <Typography fontSize={{ xs: 16, sm: 18, md: 20 }} fontWeight={400}>
              But beneath ceremony and grandeur lies fragility. The Senate brands their monarchy unstable, while Federation agents weave rebellion and unrest along the borders. Still, the Sovereignty endures, wielding fleets of unmatched speed and a legacy that refuses to fade.
            </Typography>
          </Paper>

          {/* Imperium Republic */}
          <Paper
            sx={{
              flex: 1,
              margin: { xs: '1%', md: '2%' },
              padding: { xs: '4%', md: '3%' },
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              boxShadow: 3,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography fontSize={{ xs: 20, sm: 24, md: 28 }} fontWeight={700} sx={{color: '#55DEF1', marginBottom: '2%'}}>
              Imperium Republic
            </Typography>
            <Box sx={{
              width: '100%',
              height: { xs: 150, md: 200 },
              backgroundColor: 'rgba(85, 222, 241, 0.2)',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #55DEF1',
              marginBottom: '3%'
            }}>
              <img 
                src='/images/Faction2.webp' 
                alt="Imperium Republic"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '2px',
                }}
              />
            </Box>
            <Typography fontSize={{ xs: 16, sm: 18, md: 20 }} fontWeight={400}>
              The Imperium governs forty-seven systems from its capital on Ossa Prime, bound not by bloodlines or crowns, but by process. An Assembly of Systems debates every law, every motion, every policy.
            </Typography><br/>
            <Typography fontSize={{ xs: 16, sm: 18, md: 20 }} fontWeight={400}>
              This endless bureaucracy keeps them apart from galactic wars—neutral even when their neighbors burn. Yet their ships are marvels of design, efficiency, and precision. When the Imperium moves, it does not falter.
            </Typography>
          </Paper>

          {/* Federation */}
          <Paper
            sx={{
              flex: 1,
              margin: { xs: '1%', md: '2%' },
              padding: { xs: '4%', md: '3%' },
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              boxShadow: 3,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography fontSize={{ xs: 20, sm: 24, md: 28 }} fontWeight={700} sx={{color: '#55DEF1', marginBottom: '2%'}}>
              Federation
            </Typography>
            <Box sx={{
              width: '100%',
              height: { xs: 150, md: 200 },
              backgroundColor: 'rgba(85, 222, 241, 0.2)',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #55DEF1',
              marginBottom: '3%'
            }}>
              <img 
                src='/images/Faction3.webp' 
                alt="Federation"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '2px',
                }}
              />
            </Box>
            <Typography fontSize={{ xs: 16, sm: 18, md: 20 }} fontWeight={400}>
              Once a democratic alliance, the Federation has become something far darker. Now ruled by a Council of Six, presided over by an Emperor who abandoned his throne only to seize a greater one.
            </Typography><br/>
            <Typography fontSize={{ xs: 16, sm: 18, md: 20 }} fontWeight={400}>
              From Terra Prime, they command 112 systems with brutal efficiency. To them, fear is the greatest weapon. Their gaze has turned fully toward the Sovereignty—determined to erase a monarchy they call obsolete.
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* The Guardians */}
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' }, // Center items vertically on desktop
          height: '100%',
          gap: { xs: 4, md: 4 } // Add consistent gap
        }}>
          <Box sx={{ flex: { xs: 1, md: 0.7 } }}>
            <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700}>
              THE GUARDIANS
            </Typography>
            <Typography fontSize={{ xs: 24, sm: 30, md: 40 }} fontWeight={700} sx={{color: '#55DEF1'}}>
              Born of power. Forged by fire.
            </Typography>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{marginTop: '5%'}}>
              Every two thousand years, eight Guardians are born. Always on the same day. Always marked by the Shard—a glowing sigil etched into their flesh, a living system of nanites woven into their DNA.
            </Typography><br/>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
              The Guardians are not immortal, but they are extraordinary. Their Shards grant speed, strength, and healing, while unlocking the power to shape fire, water, and raw energy. Yet every gift carries a price: the Shards draw upon their life force.
            </Typography><br/>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
              Always eight. Always one female leader. Only she can command the Solarius Stones—the crystals that power the Barrier, the Tower, and the web of gates that binds the empires together. Without her, the system collapses.
            </Typography><br/>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
              For ninety thousand years, no Guardian has ascended. Now, Max and Chloe—heirs to a broken legacy—are the first to awaken since that fall. If they cannot be found, trained, and united, the Barrier will fail... and the ancient fear will return.
            </Typography>
          </Box>
          <Box sx={{
            flex: { xs: 1, md: 0.3 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: { xs: '400px', md: '500px' } // Limit height so it doesn't stretch too much
          }}>
            {/* Placeholder for Guardian image */}
            <Box sx={{
              width: '100%',
              maxWidth: '400px', // Prevent image from getting too wide
              aspectRatio: '3/4', // Maintain consistent aspect ratio
              backgroundColor: 'rgba(85, 222, 241, 0.2)',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #55DEF1',
            }}>
              <img
                src='ChloeMax.png'
                alt="Max and Chloe"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill',
                  borderRadius: '2px',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Vaelion */}
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: { xs: 4, md: 4 }
        }}>
          {/* Main content row with image and text */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 4, md: 4 }
          }}>
            <Box sx={{
              flex: { xs: 1, md: 0.3 },
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              order: { xs: 1, md: 0 }
            }}>
              {/* Placeholder for Vaelion image */}
              <Box sx={{
                width: '100%',
                maxWidth: '400px',
                aspectRatio: '3/4',
                backgroundColor: 'rgba(85, 222, 241, 0.2)',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #55DEF1',
              }}>
                <img
                  src='/images/AIImage.png'
                  alt="Vaelion"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '2px',
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ flex: { xs: 1, md: 0.7 }, order: { xs: 0, md: 1 } }}>
              <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700}>
                VAELION
              </Typography>
              <Typography fontSize={{ xs: 20, sm: 24, md: 28 }} fontWeight={700} sx={{color: '#55DEF1'}}>
                The Last Illurian Construct
              </Typography>
              <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{marginTop: '5%'}}>
                Vaelion has waited longer than any living mind should. The last Illurian construct, he was created to guide the Guardians and preserve the Aetherium Tower. Ninety thousand years alone have sharpened his intelligence, his instincts, and especially his sarcasm.
              </Typography><br/>
              <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
                Now Vaelion communicates through three humans: the twins and their Human Protector. He is linked to the twins through the Shard woven into their DNA. He sees what they see, hears what they hear, and offers commentary whether they want it or not. His voice is dry, relentless, and brutally honest, delivered with a politeness that makes every remark more irritating.
              </Typography><br/>
              <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
                Beneath the constant observations lies a mind shaped by loss, duty, and knowledge older than the empires themselves. Vaelion pushes Max and Chloe hard, not out of cruelty, but because he knows what they must face and what they must become.
              </Typography><br/>
              <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
                He unsettles them. He interrupts them. He drives them nearly mad. But without him, they would be unprepared for the war rising ahead.
              </Typography>
            </Box>
          </Box>
          
          {/* Full-width quote section */}
          <Paper
            sx={{
              padding: { xs: '4%', md: '3%' },
              background: 'rgba(85, 222, 241, 0.2)',
              backdropFilter: 'blur(8px)',
              boxShadow: 3,
              borderLeft: '4px solid #55DEF1',
              width: '100%'
            }}
          >
            <Typography fontSize={{ xs: 16, sm: 18, md: 22 }} fontWeight={400} sx={{fontStyle: 'italic', textAlign: 'center'}}>
              "You were expecting a wise old hologram? Idiot. I'm busy keeping your galaxy from imploding. Be grateful I bothered to show up at all."
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* The Aetherium Tower */}
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' }, // Center items vertically on desktop
          height: '100%',
          gap: { xs: 4, md: 4 } // Add consistent gap
        }}>
          <Box sx={{ flex: { xs: 1, md: 0.7 } }}>
            <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700}>
              THE AETHERIUM TOWER
            </Typography>
            <Typography fontSize={{ xs: 24, sm: 30, md: 40 }} fontWeight={700} sx={{color: '#55DEF1'}}>
              Heart of a broken empire
            </Typography>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{marginTop: '5%'}}>
              Ninety thousand years ago, the world of Solrius was destroyed. Oceans boiled, cities crumbled, and the cradle of Guardian power was reduced to ash. When the fires finally died, only one structure remained: the Aetherium Tower.
            </Typography><br/>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
              The Tower was the heart of the Guardians, the seat of their power, the nexus of the Gate Network, and the source of the barrier that once shielded the Solarius Empires. But when its harmony fractured, everything collapsed. The gates fell silent. The barrier failed.
            </Typography><br/>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
              Now the gates are starting to fail again. Their strength is fading, and the barrier weakens with them. The Tower is the last hope holding the darkness back. If it falls, the empires fall with it.
            </Typography><br/>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
              All that endures is the Tower itself. Its spires stand hollow, its resonance broken, yet not empty. Deep within its silent halls waits Vaelion, the final Illurian construct. For ninety millennia he has endured, watching the ashes of a fallen age and waiting for the next Guardians to rise.
            </Typography>
          </Box>
          <Box sx={{
            flex: { xs: 1, md: 0.3 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: { xs: '400px', md: '500px' } // Limit height so it doesn't stretch too much
          }}>
            {/* Placeholder for Tower image */}
            <Box sx={{
              width: '100%',
              maxWidth: '400px', // Prevent image from getting too wide
              aspectRatio: '3/4', // Maintain consistent aspect ratio
              backgroundColor: 'rgba(85, 222, 241, 0.2)',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #55DEF1',
            }}>
              <img
                src='/images/TowerImage.png'
                alt="Tower"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '2px',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* The Aetherium Tower */}
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' }, // Center items vertically on desktop
          height: '100%',
          gap: { xs: 4, md: 4 } // Add consistent gap
        }}>
          <Box sx={{
            flex: { xs: 1, md: 0.3 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: { xs: '400px', md: '500px' } // Limit height so it doesn't stretch too much
          }}>
            {/* Placeholder for Tower image */}
            <Box sx={{
              width: '100%',
              maxWidth: '400px', // Prevent image from getting too wide
              aspectRatio: '3/4', // Maintain consistent aspect ratio
              backgroundColor: 'rgba(85, 222, 241, 0.2)',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #55DEF1',
            }}>
              <img
                src='/images/ShardImage.png'
                alt="Tower"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '2px',
                }}
              />
            </Box>
          </Box>
          <Box sx={{ flex: { xs: 1, md: 0.7 } }}>
            <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700}>
              THE SHARD
            </Typography>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{marginTop: '5%'}}>
              The Shard is older than any empire, older than the Guardians, older even than the Tower that protects it. Forged in the first age by the Illurians, it is a living system of nanites and energy woven together with a purpose no one has fully understood since the fall of Solrius. When it bonds, it does not simply choose a host. It awakens one.
            </Typography><br/>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
              Its mark appears as a glowing sigil etched into the flesh, a symbol that becomes part of the bearer’s DNA. The Shard heightens strength, speed, and healing, and unlocks the ability to bend fire, water, and raw elemental force. But every gift demands a cost. Its power feeds on the life of the one who commands it, drawing deeper the more the Guardian rises.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Call to Action */}
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Paper
          sx={{
            padding: { xs: '6%', md: '4%' },
            background: 'rgba(85, 222, 241, 0.3)',
            backdropFilter: 'blur(8px)',
            boxShadow: 3,
            textAlign: 'center'
          }}
        >
          <Typography fontSize={{ xs: 24, sm: 30, md: 40 }} fontWeight={700} sx={{marginBottom: '2%'}}>
            ENTER THE SHARD UNIVERSE
          </Typography>
          <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{marginBottom: '3%'}}>
            Discover the epic saga that spans galaxies, empires, and the very fabric of existence itself.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            sx={{
              padding: { xs: '15px 30px', md: '20px 48px' },
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              marginRight: { xs: 0, sm: '2%' },
              marginBottom: { xs: '2%', sm: 0 }
            }}
            size="large"
            href="/"
          >
            READ THE BOOKS
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default UniverseSection;